import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { productsApi } from '../../../api/products.js';
import { storesApi } from '../../../api/stores.js';
import { categoriesApi } from '../../../api/categories.js';
import { usePaginatedQuery } from '../../../hooks/usePaginatedQuery.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { DataTable } from '../../../components/DataTable.jsx';
import { Pagination } from '../../../components/ui/Pagination.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { fmt } from '../../../lib/formatters.js';
import { t } from '../../../lib/i18n.js';

import { ProductFormModal } from './ProductFormModal.jsx';

const STATUS_TONE = { active: 'success', draft: 'warning', archived: 'neutral' };
const STATUS_LABEL = { active: 'Attivo', draft: 'Bozza', archived: 'Archiviato' };

function totalStock(p) {
  if (!Array.isArray(p.inventory)) return 0;
  return p.inventory.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);
}

export function ProductsPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();

  const canCreate = hasPermission('products:create');
  const canUpdate = hasPermission('products:update');
  const canDelete = hasPermission('products:delete');

  const storesQuery = useQuery({ queryKey: ['stores', 'filter'], queryFn: () => storesApi.list({ limit: 100 }) });
  const speciesQuery = useQuery({ queryKey: ['cat', 'species', 'filter'], queryFn: () => categoriesApi.list({ kind: 'species', limit: 100 }) });
  const productCatQuery = useQuery({ queryKey: ['cat', 'productCategory', 'filter'], queryFn: () => categoriesApi.list({ kind: 'productCategory', limit: 100 }) });

  const storeMap = useMemo(() => {
    const items = (storesQuery.data && storesQuery.data.items) || [];
    return new Map(items.map((s) => [String(s._id), s]));
  }, [storesQuery.data]);

  const { items, isLoading, pagination, page, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['products', 'list'],
    fetcher: productsApi.list
  });

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: (id) => productsApi.remove(id),
    onSuccess: () => {
      toast.success('Prodotto eliminato.');
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setConfirmTarget(null);
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const columns = [
    { key: 'sku', header: 'SKU', render: (r) => <code className="rounded bg-border/40 px-1.5 py-0.5 text-xs">{r.sku}</code> },
    {
      key: 'name',
      header: 'Nome',
      render: (r) => (
        <div className="flex flex-col">
          <span className="text-ink">{r.name}</span>
          {r.brand && <span className="text-xs text-ink-muted">{r.brand}</span>}
        </div>
      )
    },
    {
      key: 'price',
      header: 'Prezzo',
      align: 'right',
      render: (r) => r.price ? <span className="text-ink">{fmt.eur(r.price.amount)}</span> : <span className="text-ink-faint">-</span>
    },
    {
      key: 'stores',
      header: 'Negozi',
      render: (r) => (r.storeIds || []).length
    },
    {
      key: 'stock',
      header: 'Scorta totale',
      align: 'right',
      render: (r) => fmt.int(totalStock(r))
    },
    {
      key: 'status',
      header: 'Stato',
      render: (r) => <Badge tone={STATUS_TONE[r.status] || 'neutral'}>{STATUS_LABEL[r.status] || r.status}</Badge>
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <div className="flex justify-end gap-2">
          {canUpdate && (
            <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => { setEditing(r); setFormOpen(true); }}>
              Modifica
            </button>
          )}
          {canDelete && (
            <button type="button" className="text-xs text-danger hover:underline" onClick={() => setConfirmTarget(r)}>
              Elimina
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('nav.products')}
        description="Catalogo prodotti, con prezzo, scorte per negozio e tassonomia."
        actions={canCreate ? <Button onClick={() => { setEditing(null); setFormOpen(true); }}>Nuovo prodotto</Button> : null}
      />

      <div className="flex flex-wrap items-end gap-3">
        <Input
          className="w-64"
          label="Cerca"
          placeholder="nome, SKU, brand..."
          value={filters.q || ''}
          onChange={(e) => setFilters({ q: e.target.value || undefined })}
        />
        <Select label="Stato" className="w-40" value={filters.status || ''} onChange={(e) => setFilters({ status: e.target.value || undefined })}>
          <option value="">Tutti</option>
          <option value="active">Attivi</option>
          <option value="draft">Bozze</option>
          <option value="archived">Archiviati</option>
        </Select>
        <Select label="Negozio" className="w-48" value={filters.storeId || ''} onChange={(e) => setFilters({ storeId: e.target.value || undefined })}>
          <option value="">Tutti</option>
          {Array.from(storeMap.values()).map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
        </Select>
        <Select label="Specie" className="w-48" value={filters.species || ''} onChange={(e) => setFilters({ species: e.target.value || undefined })}>
          <option value="">Tutte</option>
          {((speciesQuery.data && speciesQuery.data.items) || []).map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
        </Select>
        <Select label="Categoria prodotto" className="w-52" value={filters.category || ''} onChange={(e) => setFilters({ category: e.target.value || undefined })}>
          <option value="">Tutte</option>
          {((productCatQuery.data && productCatQuery.data.items) || []).map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={isLoading}
        empty={{ title: 'Nessun prodotto', description: 'Aggiungi il primo prodotto al catalogo.' }}
      />

      <Pagination pagination={pagination} onPageChange={setPage} />

      <ProductFormModal
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        initial={editing}
      />

      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title="Eliminare il prodotto?"
        description={confirmTarget ? `Il prodotto "${confirmTarget.name}" sarà rimosso dal catalogo.` : ''}
        destructive
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={() => deleteMutation.mutate(confirmTarget._id)}
      />
    </div>
  );
}
